/**
 * The Christmas shop's small interactive parts. Plain custom elements with no imports, so
 * they work on their own wherever their markup is rendered:
 *
 * - <christmas-personalize>: the "Personalize it" box on a product page
 *   (snippets/christmas-personalize.liquid). The boxes, the photo and the photo choice belong
 *   to the product form and travel with its add to cart. This only shows clear messages when
 *   something required is missing, shows the chosen photo's name, turns away a file that
 *   is not a picture or is too big, and shows or hides the engraving box as the product's
 *   "Engraving" option changes.
 *
 * - <christmas-family>: the family Christmas shirt builder (snippets/christmas-family.liquid).
 *   Pick a design and a color, say how many of each size, type the text for each shirt, and
 *   add them all to the cart in one request.
 *
 * - <christmas-upsell>: an upsell checkbox with a list of the item's variants
 *   (snippets/christmas-upsell.liquid).
 */

class ChristmasPersonalize extends HTMLElement {
  #abort = new AbortController();
  /** @type {Set<string>} the messages of one check of the form, shown together */
  #messages = new Set();
  /** @type {number | undefined} */
  #showTimer;

  connectedCallback() {
    const { signal } = this.#abort;
    // The browser raises "invalid" on each required box, photo or choice that is missing when
    // the form is checked; the messages of one check are shown together, once each.
    this.addEventListener(
      'invalid',
      (event) => {
        const input = /** @type {HTMLInputElement} */ (event.target);
        this.#messages.add(this.#messageFor(input));
        if (input.type === 'radio') input.closest('[data-choice]')?.setAttribute('aria-invalid', 'true');
        else input.setAttribute('aria-invalid', 'true');
        // One check raises them all in the same task; show them after it.
        if (this.#showTimer === undefined) this.#showTimer = window.setTimeout(() => this.#showMessages());
      },
      { capture: true, signal }
    );
    this.addEventListener(
      'input',
      (event) => {
        const input = /** @type {HTMLInputElement} */ (event.target);
        // The optional mockup checkbox is never checked, so it leaves the messages alone.
        if (input.type === 'checkbox') return;
        if (input.dataset.photo !== undefined) this.#checkPhoto(input);
        if (input.type === 'radio') input.closest('[data-choice]')?.removeAttribute('aria-invalid');
        else input.removeAttribute('aria-invalid');
        this.#clearMessages();
      },
      { signal }
    );
    // A photo kept by the browser (back button, reload) is checked again.
    const photo = this.querySelector('input[data-photo]');
    if (photo instanceof HTMLInputElement && photo.files?.length) this.#checkPhoto(photo);
    this.#followEngraving(signal);
    this.#dropEmptyOptional(signal);
  }

  // An optional engraving box (tag only, no Engraving option) left empty sends nothing. The
  // "formdata" event fires whenever the form's fields are collected: the theme's add to cart
  // (new FormData(form)) and a normal form post alike.
  #dropEmptyOptional(signal) {
    const input = this.querySelector('input[data-engraving-optional]');
    const form = input instanceof HTMLInputElement ? input.form : null;
    if (!form) return;
    form.addEventListener(
      'formdata',
      (event) => {
        if (!input.disabled && input.value.trim() === '') event.formData.delete(input.name);
      },
      { signal }
    );
  }

  // The engraving box follows the product's "Engraving" option: on while the chosen value does
  // not begin with "No". The variant picker announces each choice with the standard
  // "shopify:product:select" event, which carries the chosen options by name.
  /** @param {AbortSignal} signal */
  #followEngraving(signal) {
    const optionName = (this.dataset.engravingOption || '').trim().toLowerCase();
    if (!optionName || !this.querySelector('[data-engraving]')) return;
    const scope = this.closest('.shopify-section, dialog, product-card');
    if (!scope) return;
    /** @param {string} value */
    const isOn = (value) => !/^no/i.test(value.trim());

    // On load: the option as it stands in the picker (the browser may have kept another choice).
    const chosen = [...scope.querySelectorAll('[data-option-name]')].find(
      (element) =>
        (element.dataset.optionName || '').trim().toLowerCase() === optionName &&
        ((element instanceof HTMLInputElement && element.checked) ||
          (element instanceof HTMLOptionElement && element.selected))
    );
    if (chosen instanceof HTMLInputElement || chosen instanceof HTMLOptionElement) this.#setEngraving(isOn(chosen.value));

    scope.addEventListener(
      'shopify:product:select',
      (event) => {
        // A product card's picker inside the same section is another product.
        const card = event.target instanceof Element ? event.target.closest('product-card') : null;
        if (card && !card.contains(this)) return;
        const options = /** @type {{ selectedOptions?: { name: string, value: string }[] }} */ (event).selectedOptions;
        const option = options?.find((item) => item.name.trim().toLowerCase() === optionName);
        if (option) this.#setEngraving(isOn(option.value));
      },
      { signal }
    );
  }

  /** @param {boolean} on */
  #setEngraving(on) {
    const line = this.querySelector('[data-engraving]');
    const input = line?.querySelector('input');
    if (!(line instanceof HTMLElement) || !(input instanceof HTMLInputElement)) return;
    line.hidden = !on;
    // Disabled: not checked and not sent with the form.
    input.disabled = !on;
    if (!on) {
      input.removeAttribute('aria-invalid');
      this.#clearMessages();
    }
    if (this.dataset.engravingOnly !== undefined) this.hidden = !on;
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#abort = new AbortController();
    window.clearTimeout(this.#showTimer);
    this.#showTimer = undefined;
  }

  /** @param {HTMLInputElement} input */
  #messageFor(input) {
    if (input.type === 'radio') return this.dataset.choiceMissing || input.validationMessage;
    if (input.dataset.photo !== undefined) {
      return input.validity.customError ? input.validationMessage : this.dataset.photoMissing || input.validationMessage;
    }
    if (input.dataset.missing) return input.dataset.missing;
    return (this.dataset.message || '').replace('[label]', input.dataset.label || '');
  }

  #showMessages() {
    this.#showTimer = undefined;
    const error = this.querySelector('[data-error]');
    if (!(error instanceof HTMLElement)) return;
    error.replaceChildren(
      ...[...this.#messages].map((text) => {
        const line = document.createElement('span');
        line.textContent = text;
        return line;
      })
    );
    error.hidden = this.#messages.size === 0;
    this.#messages.clear();
  }

  #clearMessages() {
    const error = this.querySelector('[data-error]');
    if (!(error instanceof HTMLElement)) return;
    error.hidden = true;
    error.replaceChildren();
  }

  // Shows the chosen file's name, and turns away a file that is not a picture or is too big:
  // the form then stays invalid, so the add to cart cannot go through.
  /** @param {HTMLInputElement} input */
  #checkPhoto(input) {
    const file = input.files?.[0];
    const name = this.querySelector('[data-photo-name]');
    let problem = '';
    if (file) {
      const isImage = file.type.startsWith('image/') || /\.(jpe?g|png|gif|webp|heic|heif|bmp|tiff?)$/i.test(file.name);
      const maxBytes = (Number(this.dataset.maxMb) || 20) * 1024 * 1024;
      if (!isImage) problem = this.dataset.photoNotImage || '';
      else if (file.size > maxBytes) problem = this.dataset.photoTooBig || '';
    }
    input.setCustomValidity(problem);
    if (name instanceof HTMLElement) {
      const size = file ? ' (' + (file.size / 1024 / 1024).toFixed(1) + ' MB)' : '';
      name.textContent = file ? (this.dataset.photoChosen || '[name]').replace('[name]', file.name + size) : '';
      name.hidden = !file;
    }
  }
}

/**
 * @typedef {object} FamilyVariant
 * @property {number} id
 * @property {string} color - '' when the product has no color option
 * @property {string} size
 * @property {number} price - in cents
 * @property {boolean} available
 */

class ChristmasFamily extends HTMLElement {
  #abort = new AbortController();
  /** @type {any} */
  #data = null;
  /** @type {string} */
  #design = '';
  /** @type {string} */
  #color = '';
  /** @type {Map<string, string>} what was typed, by "size|n", so rows keep their text as counts change */
  #typed = new Map();
  /** @type {HTMLElement | null} */
  #caption = null;
  #busy = false;
  /** @type {Set<HTMLElement>} the design boxes laid over the gallery's main photo */
  #prints = new Set();
  /** @type {WeakSet<HTMLImageElement>} photos already listened to */
  #watched = new WeakSet();
  /** @type {Set<string>} design pictures that did not load */
  #failed = new Set();
  /** @type {ResizeObserver | null} */
  #resize = null;

  connectedCallback() {
    const source = this.querySelector('script[type="application/json"]');
    if (!source) return;
    try {
      this.#data = JSON.parse(source.textContent || '{}');
    } catch (error) {
      return;
    }
    const { signal } = this.#abort;

    // The first color that has anything to sell starts chosen.
    const colors = /** @type {string[]} */ (this.#data.colors);
    this.#color = colors.find((color) => this.#variants(color).some((v) => v.available)) || colors[0] || '';

    this.addEventListener('click', (event) => this.#onClick(event), { signal });
    this.addEventListener('input', (event) => this.#onInput(event), { signal });
    this.addEventListener('change', (event) => this.#onInput(event), { signal });
    this.#showColor();
    this.#showSizes();
    this.#showRows();
    this.#showTotals();
    this.#showCaption();
    this.#showPhoto();
    this.#showPrint();
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#abort = new AbortController();
    this.#caption?.remove();
    this.#caption = null;
    this.#resize?.disconnect();
    this.#resize = null;
    for (const box of this.#prints) {
      box.parentElement?.classList.remove('xmas-family__media');
      box.remove();
    }
    this.#prints.clear();
    this.#watched = new WeakSet();
  }

  /** @param {string} color @returns {FamilyVariant[]} */
  #variants(color) {
    return this.#data.variants.filter((/** @type {FamilyVariant} */ v) => v.color === color);
  }

  /** @param {string} size @returns {FamilyVariant | undefined} */
  #variant(size) {
    return this.#variants(this.#color).find((v) => v.size === size);
  }

  /** @param {number} cents */
  #money(cents) {
    try {
      return new Intl.NumberFormat(this.#data.locale || undefined, { style: 'currency', currency: this.#data.currency }).format(cents / 100);
    } catch (error) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  /** @returns {HTMLInputElement[]} */
  #counts() {
    return /** @type {HTMLInputElement[]} */ ([...this.querySelectorAll('input[data-size]')]);
  }

  /** @param {HTMLInputElement} input */
  #countOf(input) {
    if (input.disabled) return 0;
    const n = Math.floor(Number(input.value));
    return Number.isFinite(n) && n > 0 ? Math.min(n, Number(input.max) || 99) : 0;
  }

  /** @param {Event} event */
  #onClick(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    const design = target.closest('[data-design]');
    if (design instanceof HTMLElement) {
      this.#design = design.dataset.design || '';
      for (const tile of this.querySelectorAll('[data-design]')) tile.setAttribute('aria-pressed', String(tile === design));
      this.#showPrint();
      this.#showCaption();
      this.#clearMessage();
      return;
    }
    const color = target.closest('[data-color]');
    if (color instanceof HTMLElement) {
      this.#color = color.dataset.color || '';
      this.#showColor();
      this.#showSizes();
      this.#showRows();
      this.#showTotals();
      this.#showCaption();
      this.#showPhoto();
      this.#showPrint();
      this.#clearMessage();
      return;
    }
    if (target.closest('[data-add]')) {
      event.preventDefault();
      this.#add();
    }
  }

  /** @param {Event} event */
  #onInput(event) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    if (input.dataset.size !== undefined) {
      this.#showRows();
      this.#showTotals();
      this.#clearMessage();
    } else if (input.dataset.row !== undefined) {
      this.#typed.set(input.dataset.row, input.value);
      input.removeAttribute('aria-invalid');
      this.#clearMessage();
    }
  }

  #showColor() {
    for (const swatch of this.querySelectorAll('[data-color]')) {
      swatch.setAttribute('aria-pressed', String(/** @type {HTMLElement} */ (swatch).dataset.color === this.#color));
    }
    const name = this.querySelector('[data-color-name]');
    if (name) name.textContent = this.#color;
  }

  // Each size's price for the chosen color, "+$X" when it costs more than the cheapest, and
  // sold-out sizes switched off.
  #showSizes() {
    const here = this.#variants(this.#color);
    const priced = here.filter((v) => v.available).map((v) => v.price);
    const cheapest = priced.length ? Math.min(...priced) : Math.min(...here.map((v) => v.price));
    for (const input of this.#counts()) {
      const variant = this.#variant(input.dataset.size || '');
      const row = input.closest('[data-size-row]');
      const note = row?.querySelector('[data-size-price]');
      const soldOut = !variant || !variant.available;
      input.disabled = soldOut;
      if (soldOut) input.value = '0';
      row?.classList.toggle('xmas-family__size--off', soldOut);
      if (note) {
        note.textContent = soldOut
          ? this.#data.strings.soldOut
          : this.#money(variant.price) + (variant.price > cheapest ? ' (+' + this.#money(variant.price - cheapest) + ')' : '');
      }
    }
  }

  // One text row for every shirt counted, in size order: "Shirt 1: Adult L".
  #showRows() {
    const box = this.querySelector('[data-rows]');
    if (!box) return;
    const rows = [];
    let shirt = 0;
    for (const input of this.#counts()) {
      const size = input.dataset.size || '';
      for (let n = 1; n <= this.#countOf(input); n += 1) {
        shirt += 1;
        rows.push({ key: size + '|' + n, size, shirt });
      }
    }
    const examples = /** @type {string[]} */ (this.#data.examples || []);
    const wanted = new Set(rows.map((row) => row.key));
    for (const old of box.querySelectorAll('[data-row-wrap]')) {
      if (!wanted.has(/** @type {HTMLElement} */ (old).dataset.rowWrap || '')) old.remove();
    }
    rows.forEach((row, index) => {
      let wrap = /** @type {HTMLElement | null} */ (box.querySelector('[data-row-wrap="' + CSS.escape(row.key) + '"]'));
      if (!wrap) {
        wrap = document.createElement('div');
        wrap.className = 'xmas-family__row';
        wrap.dataset.rowWrap = row.key;
        const label = document.createElement('label');
        const input = document.createElement('input');
        input.type = 'text';
        input.maxLength = Number(this.#data.maxLength) || 20;
        input.dataset.row = row.key;
        input.dataset.sizeOf = row.size;
        input.id = this.id + '-row-' + row.key.replace(/[^a-z0-9]+/gi, '-');
        input.autocomplete = 'off';
        input.value = this.#typed.get(row.key) || '';
        label.htmlFor = input.id;
        wrap.append(label, input);
      }
      const label = /** @type {HTMLLabelElement} */ (wrap.querySelector('label'));
      const input = /** @type {HTMLInputElement} */ (wrap.querySelector('input'));
      label.textContent = this.#data.strings.shirt.replace('[n]', String(row.shirt)).replace('[size]', row.size);
      input.placeholder = examples.length ? examples[index % examples.length] : '';
      // keep the rows in size order
      if (box.children[index] !== wrap) box.insertBefore(wrap, box.children[index] || null);
    });
    const empty = this.querySelector('[data-rows-empty]');
    if (empty instanceof HTMLElement) empty.hidden = rows.length > 0;
  }

  #showTotals() {
    let shirts = 0;
    let cents = 0;
    for (const input of this.#counts()) {
      const count = this.#countOf(input);
      const variant = this.#variant(input.dataset.size || '');
      if (!count || !variant) continue;
      shirts += count;
      cents += count * variant.price;
    }
    const total = this.querySelector('[data-total]');
    if (total) {
      total.textContent = (shirts === 1 ? this.#data.strings.totalOne : this.#data.strings.totalMany.replace('[count]', String(shirts))) + ' · ' + this.#money(cents);
    }
    return { shirts, cents };
  }

  // The picked color, shown on the product picture. The picked design's name is in the same
  // caption but only for screen readers: design names are not shown on the page.
  #showCaption() {
    const gallery = this.closest('.product-information')?.querySelector('media-gallery');
    if (!gallery) return;
    if (!this.#caption) {
      this.#caption = document.createElement('p');
      this.#caption.className = 'xmas-family__caption';
      this.#caption.setAttribute('aria-live', 'polite');
      gallery.append(this.#caption);
    }
    const children = [];
    if (this.#design) {
      const name = document.createElement('span');
      name.className = 'visually-hidden';
      name.textContent = this.#color ? this.#design + ' · ' : this.#design;
      children.push(name);
    }
    if (this.#color) children.push(this.#color);
    // The design is on the photo: say it is a preview, in the design builder's words.
    const preview = this.#designSource() !== '' && this.#prints.size > 0;
    if (preview && this.#data.strings.previewLabel) {
      const note = document.createElement('span');
      note.className = 'xmas-family__caption-note';
      note.textContent = this.#data.strings.previewLabel;
      children.push(note);
    }
    this.#caption.replaceChildren(...children);
    this.#caption.hidden = children.length === 0;
    // With only a design name (no color, no preview) there is nothing to see: keep the box out
    // of sight, but still read.
    this.#caption.classList.toggle('visually-hidden', !this.#color && !preview);
  }

  // The chosen design's picture, or '' when none is chosen, it has no picture or it failed to load.
  #designSource() {
    const card = this.querySelector('[data-design][aria-pressed="true"]');
    const source = card instanceof HTMLElement ? card.dataset.image || '' : '';
    return this.#failed.has(source) ? '' : source;
  }

  // The gallery's main photo on the page (the slide and the grid copy; not the zoom view).
  /** @returns {HTMLImageElement[]} */
  #mainImages() {
    const mainId = this.#data.mainMediaId;
    const scope = this.closest('.product-information');
    if (!mainId || !scope) return [];
    const selector = `.product-media[data-media-id="${CSS.escape(String(mainId))}"] img.product-media__image`;
    return /** @type {HTMLImageElement[]} */ ([...scope.querySelectorAll(selector)].filter((image) => image instanceof HTMLImageElement && !image.closest('zoom-dialog')));
  }

  // Lays the chosen design over the main photo with the shared overlay rules
  // (snippets/christmas-print-preview-styles.liquid), placed by Theme settings > Christmas shop >
  // Family shirt design placement. With no design picture, the plain shirt shows.
  #showPrint() {
    const source = this.#designSource();
    const place = this.#data.placement || {};
    for (const image of this.#mainImages()) {
      const media = image.parentElement;
      if (!media) continue;
      let box = /** @type {HTMLElement | null} */ (media.querySelector(':scope > .xmas-family__print'));
      if (!source) {
        if (box) {
          this.#prints.delete(box);
          box.remove();
        }
        media.classList.remove('xmas-family__media');
        continue;
      }
      if (!box) {
        box = document.createElement('div');
        box.className = 'xmas-family__print';
        const design = document.createElement('img');
        design.className = 'xmas-print-design';
        design.alt = ''; // the design's name is read from the caption
        design.hidden = true;
        design.addEventListener('load', () => { design.hidden = false; });
        design.addEventListener('error', () => {
          design.hidden = true;
          this.#failed.add(design.getAttribute('src') || '');
          this.#showPrint();
          this.#showCaption();
        });
        box.append(design);
        media.classList.add('xmas-family__media');
        media.append(box);
        this.#prints.add(box);
      }
      box.style.setProperty('--design-top', (Number(place.top) || 0) + '%');
      box.style.setProperty('--design-left', (Number(place.left) || 50) + '%');
      box.style.setProperty('--design-width', (Number(place.width) || 36) + '%');
      const design = /** @type {HTMLImageElement} */ (box.querySelector('img'));
      if (design.getAttribute('src') !== source) {
        design.hidden = true;
        design.src = source;
      }
      if (!this.#watched.has(image)) {
        this.#watched.add(image);
        // A new color's photo may have another shape; place the box again once it loads.
        image.addEventListener('load', () => this.#placePrint(image), { signal: this.#abort.signal });
        this.#resize ??= new ResizeObserver((entries) => {
          for (const entry of entries) this.#placePrint(/** @type {HTMLImageElement} */ (entry.target));
        });
        this.#resize.observe(image);
      }
      this.#placePrint(image);
    }
  }

  // Sizes the design box to the part of the frame the photo really covers, so the percentages
  // are of the shirt photo and not of the gallery frame around it.
  /** @param {HTMLImageElement} image */
  #placePrint(image) {
    const box = image.parentElement?.querySelector(':scope > .xmas-family__print');
    if (!(box instanceof HTMLElement)) return;
    const width = image.clientWidth;
    const height = image.clientHeight;
    const ratio = image.naturalWidth / image.naturalHeight;
    if (!width || !height || !ratio) {
      box.hidden = true;
      return;
    }
    const style = getComputedStyle(image);
    let shownWidth = width;
    let shownHeight = height;
    if (style.objectFit === 'contain' || style.objectFit === 'scale-down') {
      if (width / height > ratio) shownWidth = height * ratio;
      else shownHeight = width / ratio;
    } else if (style.objectFit === 'cover') {
      if (width / height > ratio) shownHeight = width / ratio;
      else shownWidth = height * ratio;
    }
    const [x, y] = style.objectPosition.split(' ').map((value) => (value.endsWith('%') ? parseFloat(value) / 100 : 0.5));
    box.hidden = false;
    box.style.left = image.offsetLeft + (width - shownWidth) * x + 'px';
    box.style.top = image.offsetTop + (height - shownHeight) * y + 'px';
    box.style.width = shownWidth + 'px';
    box.style.height = shownHeight + 'px';
  }

  // The chosen color's own photo in the gallery's main photo (the slide and its zoom copy).
  // The family builder never changes the selected variant, so the gallery does not follow the
  // color by itself, and with other variants' media hidden the photo is not on the page yet.
  // A color without a photo shows the product's main photo.
  #showPhoto() {
    const mainId = this.#data.mainMediaId;
    const scope = this.closest('.product-information');
    if (!mainId || !scope) return;
    /** @type {{src: string, srcset: string, max: string, alt: string} | null} */
    const photo = (this.#data.colorPhotos || {})[this.#color] || this.#data.mainPhoto || null;
    if (!photo || !photo.src) return;
    const images = scope.querySelectorAll(`.product-media[data-media-id="${CSS.escape(String(mainId))}"] img.product-media__image`);
    for (const image of images) {
      if (!(image instanceof HTMLImageElement) || image.getAttribute('data_max_resolution') === photo.max) continue;
      // The zoom view swaps in a large copy by itself; give it this photo's large version.
      image.setAttribute('data_max_resolution', photo.max);
      if (image.closest('zoom-dialog') && !image.hasAttribute('sizes')) {
        // Already the zoom's large copy: just the large version.
        image.removeAttribute('srcset');
        image.src = photo.max;
      } else {
        if (photo.srcset) image.srcset = photo.srcset;
        else image.removeAttribute('srcset');
        image.src = photo.src;
      }
      image.alt = photo.alt;
    }
  }

  /** @param {string} text @param {HTMLElement | null} [focus] */
  #say(text, focus) {
    const message = this.querySelector('[data-message]');
    if (message instanceof HTMLElement) {
      message.textContent = text;
      message.hidden = false;
    }
    focus?.focus();
  }

  #clearMessage() {
    const message = this.querySelector('[data-message]');
    if (message instanceof HTMLElement && !this.#busy) {
      message.hidden = true;
      message.textContent = '';
    }
  }

  async #add() {
    if (this.#busy) return;
    const strings = this.#data.strings;
    const hasDesigns = this.querySelector('[data-design]') !== null;
    if (hasDesigns && !this.#design) {
      this.#say(strings.needDesign, /** @type {HTMLElement | null} */ (this.querySelector('[data-design]')));
      return;
    }
    const { shirts } = this.#showTotals();
    if (!shirts) {
      this.#say(strings.needShirts, this.#counts().find((input) => !input.disabled) || null);
      return;
    }
    const rows = /** @type {HTMLInputElement[]} */ ([...this.querySelectorAll('input[data-row]')]);
    const blank = rows.find((input) => !input.value.trim());
    if (blank) {
      blank.setAttribute('aria-invalid', 'true');
      this.#say(strings.needText, blank);
      return;
    }
    // Shirts of the same size with the same text are one line; different text is its own line.
    /** @type {Map<string, {id: number, quantity: number, properties: Record<string, string>}>} */
    const lines = new Map();
    for (const input of rows) {
      const variant = this.#variant(input.dataset.sizeOf || '');
      if (!variant || !variant.available) continue;
      const text = input.value.trim();
      const key = variant.id + '|' + text;
      const line = lines.get(key);
      if (line) line.quantity += 1;
      else {
        /** @type {Record<string, string>} */
        const properties = {};
        if (this.#design) properties[strings.designProperty] = this.#design;
        properties[strings.textProperty] = text;
        lines.set(key, { id: variant.id, quantity: 1, properties });
      }
    }
    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-add]'));
    this.#busy = true;
    if (button) button.disabled = true;
    this.#say(strings.adding);
    try {
      const response = await fetch(this.#data.addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [...lines.values()] }),
        signal: this.#abort.signal,
      });
      if (!response.ok) {
        const problem = await response.json().catch(() => ({}));
        throw new Error(problem.description || problem.message || strings.failed);
      }
      window.location.assign(this.#data.cartUrl);
    } catch (error) {
      this.#busy = false;
      if (button) button.disabled = false;
      if (error instanceof DOMException && error.name === 'AbortError') return;
      this.#say(error instanceof Error && error.message ? error.message : strings.failed);
    }
  }
}

/**
 * An upsell checkbox whose item has several variants for sale (snippets/christmas-upsell.liquid).
 * Shows the list of variants, puts the chosen one in the checkbox (the cart line) and updates the
 * price in the label. Without it the list stays hidden and the first variant for sale is added.
 */
class ChristmasUpsell extends HTMLElement {
  #abort = new AbortController();

  connectedCallback() {
    const select = this.querySelector('select[data-upsell-choice]');
    const input = this.querySelector('input[data-upsell-input]');
    const text = this.querySelector('[data-upsell-text]');
    if (!(select instanceof HTMLSelectElement) || !(input instanceof HTMLInputElement) || !text) return;

    const follow = () => {
      const option = select.selectedOptions[0];
      if (!option) return;
      input.value = option.value;
      text.textContent = (this.dataset.label ?? '').replace('[[price]]', option.dataset.price ?? '');
    };
    select.addEventListener('change', follow, { signal: this.#abort.signal });
    select.hidden = false;
    // A choice kept by the browser (back button, reload) is followed too.
    follow();
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#abort = new AbortController();
  }
}

if (!customElements.get('christmas-personalize')) customElements.define('christmas-personalize', ChristmasPersonalize);
if (!customElements.get('christmas-family')) customElements.define('christmas-family', ChristmasFamily);
if (!customElements.get('christmas-upsell')) customElements.define('christmas-upsell', ChristmasUpsell);
